import React, { FC, useEffect } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, Redirect } from 'react-router-dom';
import { FormComponentProps, FormProps } from 'antd/lib/form/Form';
import {
    Layout, Form, Icon, Input, Button, Modal,
} from 'antd';

import '../../assets/styles/App.less';
import '../../assets/styles/Login.less';

import {
    AuthState,
    login,
    getCheckLoginStatusState,
    getLoginState,
    getResetPasswordTokenState,
    resetPasswordToken,
} from '../../store/actions/auth';
import { MainReducerState } from '../../store/reducers';

interface LoginProps extends RouteComponentProps, FormComponentProps {
    onLogin: typeof login.trigger;
    onResetPasswordToken: typeof resetPasswordToken.trigger;
    resetPasswordTokenState: AuthState['resetPasswordToken'];
    checkLoginStatusState: AuthState['checkLoginStatus'];
    loginState: AuthState['login'];
}

const Login: FC<LoginProps> = ({
    checkLoginStatusState,
    form,
    loginState,
    onLogin,
    onResetPasswordToken,
    resetPasswordTokenState,
}) => {
    const { getFieldDecorator, getFieldsError, getFieldError, getFieldValue } = form;

    useEffect(() => {
        if (resetPasswordTokenState.success) {
            Modal.info({
                title: 'Demande envoyée',
                content: 'Vous allez recevoir un email avec un lien pour ré-initialiser votre mot de passe.',
            });
        }
    }, [resetPasswordTokenState.success]);

    const onFormSubmit: FormProps['onSubmit'] = (e) => {
        e.preventDefault();
        form.validateFields((err, values) => {
            if (err) {
                return;
            }
            onLogin(values);
        });
    };

    const onResetPassword = (e: any) => {
        e.preventDefault();
        if (getFieldError('email')) {
            Modal.error({
                title: 'Saisissez votre email',
                content: 'Entrez votre email dans le champs ci-dessus et recommencez.',
            });
        } else {
            const params: any = {
                email: getFieldValue('email'),
            };
            onResetPasswordToken(params);
        }
    };

    if (checkLoginStatusState.isConnected) {
        return <Redirect to="/documents" />;
    }

    let error = loginState.error ?
        'Une erreur est survenue, veuillez réessayer plus tard ou contacter un administrateur' :
        null;

    if (loginState.error && loginState.error.status && loginState.error.status === 401) {
        switch (loginState.error.status) {
            case 401:
                error = 'Non autorisé';
                break;

            case 403:
                error = 'Mot de passe incorrect';
                break;

            case 404:
                error = 'Nom d\'utilisateur introuvable';
                break;

            default:
                error = 'Une erreur est survenue, veuillez réessayer plus tard ou contacter un administrateur';
                break;
        }
    }

    const hasErrors = (fieldsError: any) => {
        return Object.keys(fieldsError).some((field) => fieldsError[field]);
    };

    return (
        <Layout id="login-layout">
            <Layout.Content>
                <header>
                    <img id="logo" src={`${process.env.PUBLIC_URL}/images/logo-marionnaud-noir.png`} alt="logo" />
                </header>
                <Form onSubmit={onFormSubmit} className="login-form">
                    <Form.Item>
                        {getFieldDecorator('email', {
                            rules: [{ required: true, message: 'Entrez votre email svp!' }],
                        })((
                            <Input
                                prefix={<Icon type="user" />}
                                placeholder="email"
                            />
                        ))}
                    </Form.Item>
                    <Form.Item>
                        {getFieldDecorator('password', {
                            rules: [{ required: true, message: 'Entrez votre mot de passe svp!' }],
                        })((
                            <Input.Password
                                prefix={<Icon type="lock" />}
                                placeholder="Mot de passe"
                            />
                        ))}
                        <a className="login-form-forgot" href="/login" onClick={onResetPassword}>
                            Mot de passe oublié ?
                        </a>
                    </Form.Item>
                    {error ? (
                        <div className="login-error has-error">
                            <span className="ant-form-explain">{error}</span>
                        </div>
                    ) : null}

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loginState.loading}
                            block
                            disabled={hasErrors(getFieldsError())}
                        >
                            Connexion
                        </Button>
                    </Form.Item>
                </Form>
            </Layout.Content>
        </Layout>
    );
};

const mapStateToProps = (state: MainReducerState) => ({
    checkLoginStatusState: getCheckLoginStatusState(state),
    resetPasswordTokenState: getResetPasswordTokenState(state),
    loginState: getLoginState(state),
});

const LoginForm = Form.create()(Login);

export default connect(
    mapStateToProps,
    {
        onLogin: login.trigger,
        onResetPasswordToken: resetPasswordToken.trigger,
    },
)(LoginForm);
